import clsx from 'clsx'
import React from 'react'
import ReactPlayer from 'react-player'

import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { Button } from 'components/common/button/Button'
import CommonFullScreenModal from 'components/common/fullScreenModal/FullScreenModal'
import Play from 'components/common/icons/play/Play'
import { Icon } from 'components/uikit/Icon/Icon'
import { Skeleton } from 'components/uikit/Skeleton/Skeleton'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './Content.module.scss'
import mainStyle from '../../../assets/css/main.module.scss'
import Diagram from '../../../assets/images/persistentPlatform/diagram.svg'
import { Flex } from '../../common/flex/Flex'

interface Props {
  createMuralIsLoading: boolean
  muralData: any
  muralIsSuccess: boolean
}

function removeSenderParam(url: string): string {
  const parsedUrl = new URL(url)
  parsedUrl.searchParams.delete('sender')
  return parsedUrl.toString()
}

const Content = ({ createMuralIsLoading, muralData, muralIsSuccess }: Props) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false)

  const videoUrl =
    'https://facade-api-tccc-dev-stage.playground-stage.coke.com/api/file-manager/v1/tenants/571f9aa5-bb8b-4f9f-8835-23e74b64e050/files/0bc5ff32-82fc-4f0d-804d-abd54f4b12b6/public-download'

  const handleCircleClick = () => {
    setIsModalOpen(true)
    setIsVideoPlaying(true)
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: EVENTS.ACTIONS.PLAYGROUND_PP_VIEWVIDEO,
    })
  }
  return (
    <>
      <Flex gap={60} className={styles.container}>
        <Flex gap={32} direction="column" justify="center">
          <div className="w-[548px]">
            <p className={clsx(mainStyle.sBody)}>
              This exercise helps distill your product’s key benefit and see how it can be translated multiple great
              creative product experience communication styles (aka lenses).
            </p>
          </div>
          <div className="w-[548px]">
            <p className={clsx(mainStyle.sStrong, 'mb-2')}>STEP 1: Build your framework</p>
            <p className={clsx(mainStyle.sBody)}>
              Download the{' '}
              <a
                href="https://cokeurl.com/persist-platf-template"
                target="_blank"
                className={styles.link}
                rel="noreferrer"
                onClick={() => {
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: EVENTS.ACTIONS.PLAYGROUND_PP_TEMPLATE,
                  })
                }}
              >
                Persistent Platform Template
              </a>{' '}
              and fill out all the parts of the framework.
            </p>
          </div>
          <div className="w-[548px]">
            <p className={clsx(mainStyle.sStrong, 'mb-2')}>STEP 2: Product Edge</p>
            <p className={clsx(mainStyle.sBody)}>
              Refer to your Product Edge, created in the Brand Vision & Architecture (BVA) which defines your product's
              key point of difference.
            </p>
          </div>
          <div className="w-[548px]">
            <p className={clsx(mainStyle.sStrong, 'mb-4')}>STEP 3: Take the quiz</p>
            <div className="flex gap-x-6 flex-nowrap">
              <div data-testid="video-placeholder" className={styles.videoPlaceholder} onClick={handleCircleClick}>
                <Play width={50} height={50} color="black" className={styles.playIcon} />
              </div>
              <div>
                <p className={clsx(mainStyle.sBody, 'mb-3')}>
                  Discover how to balance your message and inspire creativity with the right communication strategy for
                  your product.
                </p>
                {createMuralIsLoading && !muralIsSuccess && !muralData?.data.visitorsSettings?.link ? (
                  <div className="flex items-center gap-x-2">
                    <Skeleton data-testid="skeleton" height={40} width={120} className="rounded-[16px] relative" />
                  </div>
                ) : (
                  <Button
                    iconRight={<Icon icon={ChevronRight} />}
                    variant="secondary"
                    className={styles.button}
                    text="Take the quiz"
                    onClick={() => {
                      window.open(
                        `https://form.jotform.com/250301711276345?muralUrl=${removeSenderParam(muralData?.data.visitorsSettings?.link)}`,
                      )
                      trackAnalytics({
                        type: AnalyticsActionType.action,
                        payload: EVENTS.ACTIONS.PLAYGROUND_PP_QUIZ,
                      })
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="w-[548px]">
            <p className={clsx(mainStyle.sStrong, 'mb-2')}>STEP 4: Add the results to your framework</p>
            <p className={clsx(mainStyle.sBody)}>
              Once you have your Product Edge and Strategy Lens add your new Persistent Product Edge into the framework.
              Include the other three pieces into the template to fully complete your Persistent Platform Framework.
            </p>
          </div>
        </Flex>
        <div>
          <img src={Diagram} alt="diagram" />
        </div>
      </Flex>
      <CommonFullScreenModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title="Persistent Platform"
        setIsVideoPlaying={setIsVideoPlaying}
      >
        <ReactPlayer
          url={videoUrl}
          playing={isVideoPlaying}
          controls={true}
          width="100%"
          height="100%"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            flexGrow: 1,
          }}
        />
      </CommonFullScreenModal>
    </>
  )
}

export default Content
